import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { OutlookDataService } from '../../services/outlook-data.service';
import {
  IOutlookAccountSummary,
  IOutlookMailboxSettings,
  IOutlookProfile
} from '../../models/IOutlookProfile.interface';

/**
 * Who Microsoft 365 thinks the signed-in user is, plus their mailbox settings.
 *
 * Also the panel's diagnostics corner. The account row and the sign-out button are here because
 * nearly every "Outlook isn't working" report resolves to being signed in as the wrong account, and
 * this is the one place that shows which one and offers a way out of it.
 */
@Component({
  selector: 'app-outlook-profile-tab',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './outlook-profile-tab.component.html',
  styleUrls: ['./outlook-profile-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutlookProfileTabComponent implements OnInit {
  private readonly data = inject(OutlookDataService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  profile: IOutlookProfile | null = null;
  settings: IOutlookMailboxSettings | null = null;
  account: IOutlookAccountSummary | null = null;
  loading = false;

  /** Personal accounts have no mailboxSettings resource, so the section is replaced by a note. */
  isPersonal = false;

  ngOnInit(): void {
    this.account = this.data.accountSummary();
    this.isPersonal = this.data.isPersonalAccount();
    this.load();
  }

  refresh(): void {
    this.load(true);
  }

  signOut(): void {
    this.data
      .disconnect()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.profile = null;
        this.settings = null;
        this.account = null;
        this.cdr.markForCheck();
      });
  }

  get autoRepliesOn(): boolean {
    const status = this.settings?.automaticReplies?.status;
    return status === 'alwaysEnabled' || status === 'scheduled';
  }

  get autoRepliesLabel(): string {
    switch (this.settings?.automaticReplies?.status) {
      case 'alwaysEnabled':
        return 'On';
      case 'scheduled':
        return 'Scheduled';
      default:
        return 'Off';
    }
  }

  private load(force = false): void {
    this.loading = true;
    this.cdr.markForCheck();

    forkJoin({
      profile: this.data.getProfile(force),
      settings: this.data.getMailboxSettings(force)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ profile, settings }) => {
        this.profile = profile;
        this.settings = settings;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }
}
